import { profileTypeEndPoint, profileTypeEnum, tableMapData } from "@components/templates/Profile/ProfileHeader/profile-enum";
import ProfileTable from "@components/templates/Profile/ProfileTable";
import { cachingPanelData, companyEvents, tradingPartnerRangeConst } from "@containers/CompanyProfile/utils/enum.js";
import { generateTrackingObject } from "@core/global/global-country-functions";
import { trademoTrackingEvents } from "@core/global/trademo-tracking";
import { getEventName, getUserActivePlan } from "@helpers/profileHelper";
import axios from "axios";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/browser";
import { buyerCachingHandler, logisticsCachingHandler, supplierCachingHandler } from "@redux/CompanyProfile/companyProfileSlice";
import { TransitionHOC } from "@hoc/TransitionHoc/TransitionHoc";
import { globalCountryDetailsDictionary } from "@core/global/global-country-details";
import { useDebounce } from "@utils/utils";

const CompanyTable = ({ tableName, tabType = "", hideShipment = false, hideDataSource = false, hideCountry = false, setShowTabs, dataSourceColumnHeading, isSalesForce }) => {
  const [isLoaderActive, setIsLoaderActive] = useState(false);
  const [tradingPartnerData, setTradingPartnerData] = useState([]);
  const [tradingPartnerCount, setTradingPartnerCount] = useState(null);
  const [tradingPartnerMustNotInclude, setTradingPartnerMustNotInclude] = useState([]);
  const [sortOrderValue, setSortOrderValue] = useState("desc");
  const [selectedTradingPartner, setSelectedTradingPartner] = useState([]);
  const [clearSelectedTradingPartner, setClearSelectedTradingPartner] = useState(false);
  const [tradingPartnerCurrentPage, setTradingPartnerCurrentPage] = useState(1);
  const [tradingPartnerRange, setTradingPartnerRange] = useState(tradingPartnerRangeConst);
  const [searchValue, setSearchValue] = useState(null);
  const [hsCodeSearchValue, setHsCodeSearchValue] = useState(null);
  const { companyID, companyname } = useParams();
  const { tabDetails, isParentSubs, countDetails, buyerCaching, supplierCaching, logisticsCaching } = useSelector((state) => state.companyProfile);
  const { activeTabId, activePanelId } = tabDetails;
  const { search: searchUrlParams, pathname } = useLocation();
  const mergedID = new URLSearchParams(searchUrlParams).get("mergedID");
  const isMerged = new URLSearchParams(searchUrlParams).get("isMerged");
  const country = new URLSearchParams(searchUrlParams).get("country");
  const type = new URLSearchParams(searchUrlParams).get("type");
  const dataSource = new URLSearchParams(searchUrlParams).get("dataSource") || country + "-" + type;
  const dispatch = useDispatch();
  const userData = useSelector((state) => state.userDetails.userData);
  const globalPlanCheck = userData?.planIds?.find((planId) => planId?.navigate === "search" && planId?.productId === globalCountryDetailsDictionary?.global_plan?.product_id);
  const debouncedInputValue = useDebounce(searchValue, 500);
  const debouncedHsCodeValue = useDebounce(hsCodeSearchValue, 500);
  const { tableData: updatedBuyerTableData = [] } = buyerCaching[activeTabId] || {};
  const { tableData: updatedSupplierTableData = [] } = supplierCaching[activeTabId] || {};
  const { tableData: updatedLogisticsTableData = [] } = logisticsCaching[activeTabId] || {};
  const cancelTokenRef = useRef(null);

  useEffect(() => {
    setSearchValue(null);
    setHsCodeSearchValue(null);
    setTradingPartnerRange(tradingPartnerRangeConst);
    if (activePanelId === "importers") {
      setTradingPartnerData(updatedBuyerTableData);
    } else if (activePanelId === "exporters") {
      setTradingPartnerData(updatedSupplierTableData);
    } else {
      setTradingPartnerData(updatedLogisticsTableData);
    }
  }, [tableName, activeTabId, activePanelId]);

  const downloadEvent = () => {
    trademoTrackingEvents(getEventName(profileTypeEnum.company, activeTabId + "_" + companyEvents[activePanelId]?.tabDownload), generateTrackingObject(dataSource, pathname, getUserActivePlan()));
  };

  const viewShipmentsEvent = () => {
    trademoTrackingEvents(getEventName(profileTypeEnum.company, companyEvents[activePanelId]?.viewShipments + "_" + activeTabId), generateTrackingObject(dataSource, pathname, getUserActivePlan()));
  };

  const companyRedirectionEvent = (event) => {
    trademoTrackingEvents(getEventName(profileTypeEnum.company, `${companyEvents[activePanelId]?.profileClick}${event ? event : ""}`), generateTrackingObject(dataSource, pathname, getUserActivePlan()));
  };

  useEffect(() => {
    if (isParentSubs && setShowTabs) {
      setShowTabs(true);
    }
    callTrendingPartners();
  }, [isParentSubs]);

  useEffect(() => {
    setSelectedTradingPartner([]);
  }, [activePanelId, activeTabId]);

  const callTrendingPartners = (tradingPartnerData = [], tradingPartnerMustNotInclude = [], sortOrderValue = "desc", isPaginate, searchedValue = null) => {
    if (cancelTokenRef.current) {
      cancelTokenRef.current.cancel("Operation canceled due to new request.");
    }
    cancelTokenRef.current = axios.CancelToken.source();

    if (setShowTabs) setShowTabs(true);
    const requestPayload = {
      profile_type: profileTypeEnum.company,
      _id: companyID,
      category: activePanelId,
      slug: companyname,
      sub_category: activeTabId,
      map_type: "",
      order: sortOrderValue,
      must_not: tradingPartnerMustNotInclude,
      search: searchedValue !== null ? searchedValue : tableName === "HS codes" ? hsCodeSearchValue : searchValue,
    };
    setClearSelectedTradingPartner(false);
    setTradingPartnerCurrentPage(1);
    requestPayload.isSubsidiaryIncluded = isParentSubs;
    if (mergedID) {
      requestPayload.isMerged = isMerged === "false" ? false : true;
      requestPayload.mergedID = +mergedID;
    }
    setTradingPartnerData([]);
    setTradingPartnerCount(0);
    setTradingPartnerMustNotInclude([]);
    setSortOrderValue("desc");
    if (cachingPanelData.importers === activePanelId && buyerCaching[activeTabId] && buyerCaching?.[activeTabId]?.sortOrderValue === sortOrderValue && !isPaginate) {
      let buyerCachingClone = JSON.parse(JSON.stringify(buyerCaching));
      const { tableData, tableCount, sortOrderValue, mergedMustNotIncludeTradingPartner } = buyerCachingClone[activeTabId];
      setTradingPartnerData(tableData);
      setTradingPartnerCount(tableCount);
      setTradingPartnerMustNotInclude(mergedMustNotIncludeTradingPartner);
      setSortOrderValue(sortOrderValue);
      setIsLoaderActive(false);
    } else if (cachingPanelData.exporters === activePanelId && supplierCaching[activeTabId] && supplierCaching?.[activeTabId]?.sortOrderValue === sortOrderValue && !isPaginate) {
      let supplierCachingClone = JSON.parse(JSON.stringify(supplierCaching));
      const { tableData, tableCount, sortOrderValue, mergedMustNotIncludeTradingPartner } = supplierCachingClone[activeTabId];
      setTradingPartnerData(tableData);
      setTradingPartnerCount(tableCount);
      setTradingPartnerMustNotInclude(mergedMustNotIncludeTradingPartner);
      setSortOrderValue(sortOrderValue);
      setIsLoaderActive(false);
    } else if (cachingPanelData.logistic_partners === activePanelId && logisticsCaching[activeTabId] && logisticsCaching?.[activeTabId]?.sortOrderValue === sortOrderValue && !isPaginate) {
      let logisticsCachingClone = JSON.parse(JSON.stringify(logisticsCaching));
      const { tableData, tableCount, sortOrderValue, mergedMustNotIncludeTradingPartner } = logisticsCachingClone[activeTabId];
      setTradingPartnerData(tableData);
      setTradingPartnerCount(tableCount);
      setTradingPartnerMustNotInclude(mergedMustNotIncludeTradingPartner);
      setSortOrderValue(sortOrderValue);
      setIsLoaderActive(false);
    } else {
      setIsLoaderActive(true);
      setTradingPartnerData(null);
      axios
        .post(profileTypeEndPoint.ports.getShipperDetails, requestPayload, {
          cancelToken: cancelTokenRef.current.token,
        })
        .then((res) => {
          const { data, status } = res.data;
          if (!!status && status.toLocaleUpperCase() === "success".toLocaleUpperCase()) {
            const tableData = data[tableMapData[activeTabId].data];
            const tableCount = data[tableMapData[activeTabId].count];
            const mustNot = data.must_not;
            if (setShowTabs) setShowTabs(true); // this is for parent company

            if (!!data && !!tableData && tableData.length !== 0) {
              let mergedMustNotIncludeTradingPartner = tradingPartnerMustNotInclude.concat(mustNot);
              let mergedTradingPartnerData = tradingPartnerData.concat(tableData);
              if (tableCount !== undefined && tableCount < 100) {
                setTradingPartnerRange({ ...tradingPartnerRangeConst, to: tableCount });
              }
              setTradingPartnerData(mergedTradingPartnerData);
              setTradingPartnerCount(tableCount);
              setTradingPartnerMustNotInclude(mergedMustNotIncludeTradingPartner);
              setSortOrderValue(sortOrderValue);
              if (cachingPanelData.importers === activePanelId && !searchValue && !hsCodeSearchValue) {
                let buyerCachingClone = JSON.parse(JSON.stringify(buyerCaching));
                buyerCachingClone[activeTabId] = { tableData: mergedTradingPartnerData, tableCount: tableCount, sortOrderValue: sortOrderValue, mergedMustNotIncludeTradingPartner: mergedMustNotIncludeTradingPartner };
                dispatch(buyerCachingHandler(buyerCachingClone));
              } else if (cachingPanelData.exporters === activePanelId && !searchValue && !hsCodeSearchValue) {
                let supplierCachingClone = JSON.parse(JSON.stringify(supplierCaching));
                supplierCachingClone[activeTabId] = { tableData: mergedTradingPartnerData, tableCount: tableCount, sortOrderValue: sortOrderValue, mergedMustNotIncludeTradingPartner: mergedMustNotIncludeTradingPartner };
                dispatch(supplierCachingHandler(supplierCachingClone));
              } else if (cachingPanelData.logistic_partners === activePanelId && !searchValue && !hsCodeSearchValue) {
                let logisticsCachingClone = JSON.parse(JSON.stringify(logisticsCaching));
                logisticsCachingClone[activeTabId] = { tableData: mergedTradingPartnerData, tableCount: tableCount, sortOrderValue: sortOrderValue, mergedMustNotIncludeTradingPartner: mergedMustNotIncludeTradingPartner };
                dispatch(logisticsCachingHandler(logisticsCachingClone));
              }
            } else {
              setTradingPartnerData([]);
              setTradingPartnerCount(0);
              setTradingPartnerMustNotInclude([]);
              setSortOrderValue("desc");
              if (setShowTabs) setShowTabs(false);
            }
            setIsLoaderActive(false);
          } else {
            toast.error("Something went wrong. Please try again after sometime !!!");
          }
        })
        .catch((error) => {
          if (!axios.isCancel(error)) {
            setIsLoaderActive(false);
            Sentry.captureException(error);
            toast.error("Something went wrong. Please try again later !!!");
          }
        })
        .finally(() => {
          setIsLoaderActive(false);
        });
    }
  };

  useEffect(() => {
    if (debouncedInputValue !== null && (debouncedInputValue === "" || debouncedInputValue.length > 1)) {
      callTrendingPartners([], [], sortOrderValue, true, searchValue);
    }
  }, [debouncedInputValue]);

  useEffect(() => {
    if (debouncedHsCodeValue !== null && (debouncedHsCodeValue === "" || debouncedHsCodeValue.length > 1)) {
      callTrendingPartners([], [], sortOrderValue, true, hsCodeSearchValue);
    }
  }, [debouncedHsCodeValue]);

  const onSearchHandler = (value) => {
    setSearchValue(value);
  };

  const onHsCodeSearchHandler = (value) => {
    setHsCodeSearchValue(value);
  };

  return (
    <TransitionHOC>
      {tradingPartnerData?.length === 0 && countDetails?.is_parent_company && !countDetails?.loading && !isLoaderActive && !isParentSubs && !searchValue && !hsCodeSearchValue ? (
        <div className="label-md text-center no-shipment-data">
          <img src="https://res.cloudinary.com/drzta9shk/image/upload/v1686817425/intel/icons/brightness_alert_lwi77k.svg" />
          <p>We can't find any shipments for this company. There may be shipments being imported by its subsidiaries. Please switch on the "Include Subsidiaries Data" option above.</p>
        </div>
      ) : (
        <ProfileTable
          isParentSubs={isParentSubs}
          tableName={tableName}
          tabType={tabType}
          callTrendingPartners={callTrendingPartners}
          isLoaderActive={isLoaderActive}
          tradingPartnerData={tradingPartnerData}
          tradingPartnerCount={tradingPartnerCount}
          sortOrderValue={sortOrderValue}
          tradingPartnerMustNotInclude={tradingPartnerMustNotInclude}
          tradingPartnerRange={tradingPartnerRange}
          setTradingPartnerRange={setTradingPartnerRange}
          category={activePanelId}
          subCategory={activeTabId}
          profileName={companyname}
          companyName={countDetails.name}
          profileType={profileTypeEnum.company}
          profileId={companyID}
          selectedTradingPartner={selectedTradingPartner}
          setSelectedTradingPartner={setSelectedTradingPartner}
          clearSelectedTradingPartner={clearSelectedTradingPartner}
          setClearSelectedTradingPartner={setClearSelectedTradingPartner}
          tradingPartnerCurrentPage={tradingPartnerCurrentPage}
          setTradingPartnerCurrentPage={setTradingPartnerCurrentPage}
          downloadEvent={downloadEvent}
          dataSource={dataSource}
          viewShipmentsEvent={viewShipmentsEvent}
          companyRedirectionEvent={companyRedirectionEvent}
          hideShipment={hideShipment}
          hideDataSource={hideDataSource}
          hideCountry={hideCountry}
          profileRedirectionId={(countDetails && Array.isArray(countDetails.elastic_data_id) && countDetails.elastic_data_id.join("$$$")) || companyID}
          globalPlan={globalPlanCheck?.navigate === "search" ? true : false}
          dataSourceColumnHeading={dataSourceColumnHeading}
          searchValue={searchValue}
          hsCodeSearchValue={hsCodeSearchValue}
          onSearchHandler={onSearchHandler}
          onHsCodeSearchHandler={onHsCodeSearchHandler}
          isSalesForce={isSalesForce}
        />
      )}
    </TransitionHOC>
  );
};

export default CompanyTable;
